import {
  CARD_TRIAL_DAYS,
  ONBOARDING_CALL_CREDITS,
  PRO_TRIAL_CREDITS,
} from '@/lib/card-trial';
import { TESTIMONIAL_ROTATE_MS, TESTIMONIALS } from '@/constants/testimonials';
import { cn } from '@/lib/utils';
import { useReducedMotion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { siteUrl } from '@/lib/brand';

/**
 * Right-hand panel of the auth pages. Mirrors the proof band on cleanlist.ai
 * (landing-page-2 ProofBand): customer quotes, third-party scores, the
 * Product Hunt badge and customer marks with their "switched from" story.
 * Keep the numbers in step with the website when they change there.
 */

const WEBSITE = siteUrl('https://www.cleanlist.ai');

const SCORES = [
  {
    source: 'G2',
    mark: '/logos/g2.png',
    score: 5.0,
    reviews: 5,
    href: 'https://www.g2.com/products/cleanlist-ai/reviews',
  },
  {
    source: 'Chrome Store',
    mark: '/logos/chrome-web-store-mark.png',
    score: 4.7,
    reviews: 13,
    href: 'https://chromewebstore.google.com/detail/cleanlistai/njbepggbhklfpljfcmpnonjdolibjadj',
  },
  {
    source: 'AppSumo',
    mark: '/logos/appsumo-mark.png',
    score: 4.8,
    reviews: 112,
    href: 'https://appsumo.com/products/cleanlist-ai/',
  },
];

const RIVAL_MARK = {
  ZoomInfo: '/logos/zoominfo-mark.png',
  Apollo: '/logos/apollo-mark.png',
  Clay: '/logos/clay-mark.png',
} as const;

type Rival = keyof typeof RIVAL_MARK;

// Wordmarks are painted as CSS masks so every logo lands in one colour; w/h
// are the website's optical sizes so a 9:1 and a 2:1 mark carry the same weight.
const CUSTOMERS: {
  name: string;
  src: string;
  w: number;
  h: number;
  caseStudy?: string;
  switchedFrom?: Rival;
}[] = [
  {
    name: 'Proposify',
    src: '/logos/proposify.png',
    w: 96,
    h: 20,
    caseStudy: 'proposify',
  },
  { name: 'Keep', src: '/logos/keep.png', w: 67, h: 19, switchedFrom: 'Clay' },
  {
    name: 'Kindsight',
    src: '/logos/kindsight.png',
    w: 77,
    h: 18,
    switchedFrom: 'ZoomInfo',
  },
  { name: 'Float', src: '/logos/float.png', w: 56, h: 24, caseStudy: 'float' },
  {
    name: 'Upfluence',
    src: '/logos/upfluence.png',
    w: 78,
    h: 17,
    switchedFrom: 'Apollo',
  },
  { name: 'Warp', src: '/logos/warp.png', w: 84, h: 21, caseStudy: 'warp' },
  {
    name: 'BuildZoom',
    src: '/logos/buildzoom.png',
    w: 111,
    h: 16,
    switchedFrom: 'Clay',
  },
  {
    name: 'HelpJuice',
    src: '/logos/helpjuice.png',
    w: 70,
    h: 20,
    switchedFrom: 'Apollo',
  },
];

const PRODUCT_HUNT_HREF =
  'https://www.producthunt.com/products/cleanlist-ai?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_campaign=badge-cleanlist-ai-3';

/** Shared with the trial page's quote, which rotates on the same beat. */
const ROTATE_MS = TESTIMONIAL_ROTATE_MS;

const STAR_PATH =
  'M6.5 0l1.95 4.1 4.55.5-3.4 3.05.95 4.45L6.5 9.8 2.45 12.1l.95-4.45L0 4.6l4.55-.5z';

const StarRow = ({ className }: { className?: string }) => (
  <span className={cn('flex gap-[3px]', className)}>
    {Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        viewBox='0 0 13 13'
        className='h-3 w-3 shrink-0'
        fill='currentColor'
      >
        <path d={STAR_PATH} />
      </svg>
    ))}
  </span>
);

const Stars = ({ score }: { score: number }) => (
  <span className='relative block w-fit' aria-hidden='true'>
    <StarRow className='text-white/15' />
    <span
      className='absolute inset-y-0 left-0 overflow-hidden'
      style={{ width: `${(score / 5) * 100}%` }}
    >
      <StarRow className='text-[#ffb020]' />
    </span>
  </span>
);

const ProductHuntBadge = () => (
  <a
    href={PRODUCT_HUNT_HREF}
    target='_blank'
    rel='noopener noreferrer'
    className='inline-flex h-[50px] w-fit items-center gap-3 rounded-xl bg-white/[0.04] pr-3.5 pl-2.5 ring-1 ring-white/12 transition ring-inset hover:ring-[#ff6154]/55'
  >
    <img src='/logos/producthunt-mark.png' alt='' className='h-8 w-8' />
    <span className='flex flex-col text-left leading-tight'>
      <span className='text-[10px] font-semibold tracking-[0.09em] text-white/50 uppercase'>
        Product Hunt
      </span>
      <span className='text-sm font-semibold tracking-tight text-white'>
        #2 Product of the Day
      </span>
    </span>
    <svg
      viewBox='0 0 24 24'
      className='h-5 w-5 text-[#f0a24a]'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <circle cx='12' cy='14.5' r='6' />
      <path d='M8.5 9.5 6 3h4l2 4 2-4h4l-2.5 6.5M12 12.5v4' />
    </svg>
  </a>
);

const Testimonials = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (paused || reduceMotion) return;
    const timer = setInterval(
      () => setActive((i) => (i + 1) % TESTIMONIALS.length),
      ROTATE_MS
    );
    return () => clearInterval(timer);
  }, [paused, reduceMotion]);

  const t = TESTIMONIALS[active];

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <figure
        key={active}
        className='flex min-h-[172px] animate-in flex-col duration-500 fade-in slide-in-from-bottom-1 motion-reduce:animate-none'
      >
        <blockquote className='text-[22px] leading-[1.4] font-medium tracking-[-0.015em] text-white'>
          “{t.quote}”
        </blockquote>
        <figcaption className='mt-auto flex items-center gap-3 pt-6'>
          <img
            src={t.avatar}
            alt=''
            className='h-11 w-11 rounded-full object-cover ring-1 ring-white/15'
          />
          <div className='min-w-0 flex-1'>
            <p className='text-sm font-semibold text-white'>{t.name}</p>
            <p className='truncate text-[13px] text-white/55'>
              {t.role}, {t.company}
            </p>
          </div>
          {t.metric ? (
            <div className='text-right'>
              <p className='text-xl font-semibold tracking-tight text-[#8fb6ff] tabular-nums'>
                {t.metric.value}
              </p>
              <p className='text-xs text-white/55'>{t.metric.label}</p>
            </div>
          ) : null}
        </figcaption>
      </figure>

      <div className='mt-5 flex gap-1.5'>
        {TESTIMONIALS.map((item, i) => (
          <button
            key={item.name}
            type='button'
            onClick={() => setActive(i)}
            aria-label={`Show quote from ${item.name}, ${item.company}`}
            aria-current={i === active}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              i === active ? 'bg-white/80' : 'bg-white/15 hover:bg-white/30'
            )}
          />
        ))}
      </div>
    </div>
  );
};

const Ratings = () => (
  <div className='grid grid-cols-3 divide-x divide-white/10'>
    {SCORES.map((s) => (
      <a
        key={s.source}
        href={s.href}
        target='_blank'
        rel='noopener noreferrer'
        className='group flex items-center gap-3 px-5 first:pl-0 last:pr-0'
        aria-label={`${s.score.toFixed(1)} out of 5 on ${s.source}, from ${s.reviews} reviews`}
      >
        <span className='text-[32px] leading-none font-semibold tracking-[-0.04em] text-white tabular-nums'>
          {s.score.toFixed(1)}
        </span>
        <span className='flex min-w-0 flex-col gap-1.5'>
          <Stars score={s.score} />
          <span className='flex items-center gap-1.5 text-xs text-white/55 transition-colors group-hover:text-white/80'>
            <img
              src={s.mark}
              alt=''
              className='h-3.5 w-3.5 shrink-0 object-contain'
            />
            <span className='whitespace-nowrap'>{s.source}</span>
          </span>
        </span>
      </a>
    ))}
  </div>
);

const CELL_CLASS =
  'group flex h-[88px] flex-col items-center justify-center gap-2.5 bg-[#0e1015] transition-colors hover:bg-[#14171e]';

const CustomerCell = ({ customer: c }: { customer: (typeof CUSTOMERS)[0] }) => (
  <>
    <span
      role='img'
      aria-label={c.name}
      className='block bg-white/60 transition-colors group-hover:bg-white'
      style={{
        width: c.w,
        height: c.h,
        maskImage: `url(${c.src})`,
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
      }}
    />
    {c.caseStudy ? (
      <span className='text-[11px] font-medium text-[#8fb6ff]'>
        Case study →
      </span>
    ) : (
      c.switchedFrom && (
        <span className='flex items-center gap-1 text-[11px] text-white/45'>
          Switched from
          <img
            src={RIVAL_MARK[c.switchedFrom]}
            alt=''
            className='h-3 w-3 rounded-[3px] object-contain'
          />
          {c.switchedFrom}
        </span>
      )
    )}
  </>
);

const CustomerGrid = () => (
  <div>
    <div className='mb-4 flex items-center gap-4 text-[13px] font-medium text-white/60'>
      <span>
        Used by <span className='font-semibold text-white'>2,000+</span> GTM
        teams
      </span>
      <span className='h-px flex-1 bg-linear-to-r from-white/12 to-transparent' />
    </div>
    <div className='grid grid-cols-4 gap-px overflow-hidden rounded-xl bg-white/[0.07] ring-1 ring-white/[0.07]'>
      {CUSTOMERS.map((c) =>
        c.caseStudy ? (
          <a
            key={c.name}
            href={`${WEBSITE}/customers/${c.caseStudy}`}
            target='_blank'
            rel='noopener noreferrer'
            className={CELL_CLASS}
          >
            <CustomerCell customer={c} />
          </a>
        ) : (
          <div key={c.name} className={CELL_CLASS}>
            <CustomerCell customer={c} />
          </div>
        )
      )}
    </div>
  </div>
);

interface AuthPreviewCarouselProps {
  /** `appsumo`: a register page for a lifetime deal, which has no trial. */
  variant?: 'login' | 'register' | 'appsumo';
}

export const AuthPreviewCarousel = ({
  variant = 'register',
}: AuthPreviewCarouselProps) => (
  <div className='flex flex-col gap-10 py-4'>
    <ProductHuntBadge />
    <Testimonials />
    <Ratings />
    <CustomerGrid />
    {variant === 'register' && (
      // "Eligible": the trial needs a card and isn't offered in every region,
      // and support saw sign-ups expect it regardless (Replay Vision).
      <p className='text-center text-[13px] text-white/55'>
        Eligible sign-ups get{' '}
        <span className='font-medium text-white'>
          Pro free for {CARD_TRIAL_DAYS} days
        </span>{' '}
        · {PRO_TRIAL_CREDITS} credits · +{ONBOARDING_CALL_CREDITS} with an
        onboarding call
      </p>
    )}
  </div>
);
